import { createContext, useContext, useState, type ReactNode } from "react";
import { Bug } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { User } from "@gb/contracts";
import { api, errorMessage } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { cn } from "@/lib/utils";

/**
 * The debug view's switch (`architecture/0029`).
 *
 * **Whether the switch exists is the server's answer, read fresh.** The user
 * stored at sign-in may predate the flag, and the platform's admin can turn it
 * on or off at any time, so `canDebug` comes from `/auth/me` rather than from
 * the stored copy. The switch is only drawn; the debug routes answer 404 to
 * anyone without the flag, whatever this file believes.
 *
 * **On or off is remembered per browser,** because it is a viewing preference.
 * Storage can be unavailable (a private window), so every read and write is
 * guarded and the default is off.
 */
const DEBUG_KEY = "gb.debug";

type DebugMode = {
  /** The account may turn it on. */
  available: boolean;
  /** It is on, and the account may. */
  on: boolean;
  setOn: (on: boolean) => void;
};

const DebugContext = createContext<DebugMode>({ available: false, on: false, setOn: () => undefined });

function readStored(): boolean {
  try {
    return localStorage.getItem(DEBUG_KEY) === "on";
  } catch {
    return false;
  }
}

function writeStored(on: boolean): void {
  try {
    if (on) localStorage.setItem(DEBUG_KEY, "on");
    else localStorage.removeItem(DEBUG_KEY);
  } catch {
    // A preference that cannot be kept is lost on reload, which is fine.
  }
}

/** The signed-in user as the server says now, for its `canDebug`. */
export function useMe() {
  const { accessToken, status } = useAuth();
  return useQuery({
    queryKey: ["auth", "me"],
    enabled: status === "authenticated" && Boolean(accessToken),
    staleTime: 60_000,
    queryFn: async (): Promise<User> => {
      const res = await api.auth.me({ headers: { authorization: `Bearer ${accessToken ?? ""}` } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.user;
    },
  });
}

export function DebugProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const available = me.data?.canDebug === true;
  const [on, setOnState] = useState(readStored);
  const setOn = (value: boolean) => {
    setOnState(value);
    writeStored(value);
  };
  return (
    <DebugContext.Provider value={{ available, on: available && on, setOn }}>{children}</DebugContext.Provider>
  );
}

export function useDebugMode(): DebugMode {
  return useContext(DebugContext);
}

/** Drawn only for an account that may debug. */
export function DebugSwitch() {
  const { available, on, setOn } = useDebugMode();
  if (!available) return null;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => setOn(!on)}
      title={on ? "Hide how the system reasoned" : "Show how the system reasoned, beside each file, import and answer"}
      className={cn(
        "flex min-h-[34px] cursor-pointer items-center gap-1.5 rounded-control border-[1.5px] px-2.5 text-[0.78rem] font-semibold transition-colors",
        on
          ? "border-warn/60 bg-warn-bg text-warn-text"
          : "border-border bg-surface text-muted hover:border-border-hover hover:text-ink",
      )}
    >
      <Bug size={14} aria-hidden />
      Debug {on ? "on" : "off"}
    </button>
  );
}
